import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "medskin_token";
const ADMIN_TOKEN_KEY = "medskin_admin_token";
const ACTIVE_PROFILE_KEY = "medskin_active_profile";

export const activeProfile = {
  get: () => sessionStorage.getItem(ACTIVE_PROFILE_KEY) || "",
  set: (id) => (id ? sessionStorage.setItem(ACTIVE_PROFILE_KEY, id) : sessionStorage.removeItem(ACTIVE_PROFILE_KEY)),
  clear: () => sessionStorage.removeItem(ACTIVE_PROFILE_KEY),
};

export const tokenStore = {
  get: () => localStorage.getItem(TOKEN_KEY),
  set: (t) => localStorage.setItem(TOKEN_KEY, t),
  clear: () => localStorage.removeItem(TOKEN_KEY),
};

// Default to a 25s timeout on every API call — long enough for the largest
// payload (/api/products ≈ 400 KB JSON) on slow mobile networks, short
// enough that a truly stalled request surfaces as a real error instead of
// the user staring at a spinner forever.
export const api = axios.create({ baseURL: API, timeout: 25000 });

api.interceptors.request.use((config) => {
  // Prefer admin token if present (admin user has all the rights of regular
  // users plus admin endpoints) — falls back to the user magic-link token.
  // EXCEPTION: identity-bound calls (e.g. payment checkout) set
  // `forceUserToken` so they always bind to the signed-in USER session and
  // never the admin token — otherwise an admin-token-in-browser would bill
  // and prefill the admin account instead of the actual user.
  const adminToken = localStorage.getItem(ADMIN_TOKEN_KEY);
  const customerRoute = !String(config.url || "").includes("/admin/");
  const token = (config.forceUserToken || customerRoute) ? tokenStore.get() : (adminToken || tokenStore.get());
  if (token) config.headers.Authorization = `Bearer ${token}`;
  const pid = sessionStorage.getItem(ACTIVE_PROFILE_KEY);
  if (pid) config.headers["X-Profile-Id"] = pid;
  return config;
});

// --------------------------------------------------------------------------- #
// 401 handler for admin endpoints — when the admin Bearer token has expired
// (or has been revoked), clear it and bounce the user back to /admin so they
// can re-authenticate. Skips /admin/auth/login (a 401 there means wrong
// password, not session expiry). De-duped via a session-scoped flag so a
// burst of parallel admin requests yields one toast + one redirect.
// --------------------------------------------------------------------------- #
let adminRedirectInFlight = false;

api.interceptors.response.use(
  (resp) => resp,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || "";
    const isAdminEndpoint = url.includes("/admin/")
      && !url.includes("/admin/auth/login");

    if (status === 401 && isAdminEndpoint && !adminRedirectInFlight) {
      adminRedirectInFlight = true;
      try { localStorage.removeItem(ADMIN_TOKEN_KEY); } catch (_) { /* ignore */ }
      toast.error("Admin session expired", {
        description: "Please sign in again to continue.",
      });
      // Defer navigation a tick so the toast renders before the redirect,
      // and so any synchronously-pending callers see the rejected promise
      // before the route changes.
      const target = "/admin";
      const here = window.location.pathname || "";
      if (here !== target && !here.startsWith("/admin/login")) {
        setTimeout(() => {
          window.location.assign(target);
        }, 150);
      } else {
        // Already on /admin — just reset the flag on the next tick so the
        // user can retry login.
        setTimeout(() => { adminRedirectInFlight = false; }, 500);
      }
    }

    // Self-heal a stale/invalid active profile: if a request carried an
    // X-Profile-Id the server no longer recognises, clear it and retry once so
    // core pages (checker, routines, preferences) fall back to the default
    // profile instead of silently 404-ing.
    const sentPid = error?.config?.headers?.["X-Profile-Id"];
    const detail = error?.response?.data?.detail;
    if (status === 404 && sentPid && typeof detail === "string"
        && detail.startsWith("Profile not found") && !error.config.__profileRetried) {
      try { sessionStorage.removeItem(ACTIVE_PROFILE_KEY); } catch (_) { /* ignore */ }
      error.config.__profileRetried = true;
      try { delete error.config.headers["X-Profile-Id"]; } catch (_) { /* ignore */ }
      return api.request(error.config);
    }
    return Promise.reject(error);
  },
);

export async function requestMagicLink(email) {
  const { data } = await api.post("/auth/magic-link", { email });
  return data; // { magic_link, token, expires_at }
}

export async function verifyMagicLink(token) {
  const { data } = await api.post("/auth/verify", { token });
  return data; // { access_token, user }
}

export async function fetchMe() {
  const { data } = await api.get("/auth/me", { forceUserToken: true });
  return data;
}

// ─── Email + password accounts ────────────────────────────────────────────────
export async function registerAccount(email, password) {
  const { data } = await api.post(
    "/auth/register", { email, password }, { forceUserToken: true },
  );
  return data; // { access_token, user }
}
export async function loginWithPassword(email, password) {
  const { data } = await api.post(
    "/auth/login", { email, password }, { forceUserToken: true },
  );
  return data; // { access_token, user }
}
export async function requestPasswordReset(email) {
  const { data } = await api.post("/auth/forgot-password", { email });
  return data; // { ok, message }
}
export async function resetPassword(token, password) {
  const { data } = await api.post(
    "/auth/reset-password", { token, password }, { forceUserToken: true },
  );
  return data; // { access_token, user }
}

// ─── Waitlist ────────────────────────────────────────────────────────────────

export async function subscribeWaitlist({ email, first_name, source = "organic" }) {
  // Attribution context (UTMs + referrer + landing path) is captured on
  // first pageview by ../lib/attribution.js. We forward it transparently
  // so every signup row carries the campaign that originally drove it.
  // Lazy import so the API client stays usable in node / SSR contexts
  // that don't have window/localStorage.
  let attribution = {};
  try {
    const mod = await import("./attribution");
    attribution = mod.getAttribution ? mod.getAttribution() : {};
  } catch (_e) {
    attribution = {};
  }
  const { data } = await api.post("/waitlist/subscribe", {
    email,
    first_name,
    source,
    ...attribution,
  });
  return data;
}

export async function confirmWaitlist(token) {
  const { data } = await api.post("/waitlist/confirm", { token });
  return data;
}

// ─── Customer capture (gap-notify, save-check, unsubscribe, admin) ────────────
export async function subscribeGapNotify(payload) {
  const { data } = await api.post("/capture/gap-notify", payload);
  return data;
}
export async function saveCheck(payload) {
  const { data } = await api.post("/capture/save-check", payload);
  return data;
}
export async function fetchAdminContacts({ q = "", source = "", limit = 200 } = {}) {
  const params = new URLSearchParams();
  if (q) params.set("q", q);
  if (source) params.set("source", source);
  params.set("limit", String(limit));
  const { data } = await api.get(`/admin/contacts?${params.toString()}`);
  return data;
}
export async function deleteContact(email) {
  const { data } = await api.delete(`/admin/contacts/${encodeURIComponent(email)}`);
  return data;
}
export async function downloadContactsCsv() {
  const res = await api.get("/admin/contacts/export.csv", { responseType: "blob" });
  return res.data;
}
export async function fetchEmailLog({ trigger = "", start = "", end = "", limit = 300 } = {}) {
  const params = new URLSearchParams();
  if (trigger) params.set("trigger", trigger);
  if (start) params.set("start", start);
  if (end) params.set("end", end);
  params.set("limit", String(limit));
  const { data } = await api.get(`/admin/contacts/email-log?${params.toString()}`);
  return data;
}
export async function downloadEmailLogCsv({ trigger = "", start = "", end = "" } = {}) {
  const params = new URLSearchParams();
  if (trigger) params.set("trigger", trigger);
  if (start) params.set("start", start);
  if (end) params.set("end", end);
  const res = await api.get(`/admin/contacts/email-log.csv?${params.toString()}`, { responseType: "blob" });
  return res.data;
}

// --- Profiles ---
export async function fetchProfiles() {
  const { data } = await api.get("/profiles", { forceUserToken: true });
  return data;
}
export async function createProfile(name) {
  const { data } = await api.post("/profiles", { name }, { forceUserToken: true });
  return data;
}
export async function renameProfile(id, name) {
  const { data } = await api.put(`/profiles/${id}`, { name }, { forceUserToken: true });
  return data;
}
export async function deleteProfile(id) {
  const { data } = await api.delete(`/profiles/${id}`, { forceUserToken: true });
  return data;
}

// --- Share report with clinic ---
export async function shareReportWithClinic(result, toEmail, note) {
  const { data } = await api.post("/report/share", { result, to_email: toEmail, note: note || null }, { forceUserToken: true });
  return data;
}

// --- Public runtime config ---
export async function fetchPublicConfig() {
  const { data } = await api.get("/config");
  return data;
}
export async function fetchGapDemand() {
  const { data } = await api.get("/capture/admin/gap-demand");
  return data;
}
export async function sendPendingGapNotifications() {
  const { data } = await api.post("/capture/admin/send-pending-gap-notifications", {});
  return data;
}

export async function fetchWaitlistCount() {
  // Returns { count, real_count, display }
  const { data } = await api.get("/waitlist/count");
  return data;
}

export async function fetchAdminWaitlist({ q = "", limit = 100 } = {}) {
  const params = {};
  if (q) params.q = q;
  if (limit) params.limit = limit;
  const { data } = await api.get("/admin/v2/waitlist", { params });
  return data;
}


export async function fetchRecentPublicWaitlist({ limit = 12 } = {}) {
  const { data } = await api.get("/waitlist/recent-public", { params: { limit } });
  return data;
}

// ── Admin: founding-member hygiene + per-row actions ──────────────────────────
export async function fetchWaitlistHygiene() {
  const { data } = await api.get("/admin/v2/waitlist/hygiene");
  return data;
}
export async function fetchWaitlistTestRows() {
  const { data } = await api.get("/admin/v2/waitlist/hygiene/test-rows");
  return data;
}
export async function deleteWaitlistTestRows() {
  const { data } = await api.post("/admin/v2/waitlist/hygiene/delete-test-rows", { confirm: true });
  return data;
}
export async function sendWaitlistTestEmail() {
  const { data } = await api.post("/admin/v2/waitlist/send-test-email", {});
  return data;
}
export async function resendWaitlistConfirmation(email) {
  const { data } = await api.post(`/admin/v2/waitlist/${encodeURIComponent(email)}/resend`, {});
  return data;
}
export async function markWaitlistTest(email) {
  const { data } = await api.post(`/admin/v2/waitlist/${encodeURIComponent(email)}/mark-test`, {});
  return data;
}
export async function deleteWaitlistRow(email) {
  const { data } = await api.delete(`/admin/v2/waitlist/${encodeURIComponent(email)}`);
  return data;
}
export async function downloadWaitlistCsv() {
  const res = await api.get("/admin/v2/waitlist/export.csv", { responseType: "blob" });
  return res.data;
}


export async function fetchMedications() {
  // One automatic retry — meds are the critical-path payload for the
  // safety checker; if the first GET aborts on a flaky mobile connection
  // we'd rather retry once silently than show the user an error toast.
  try {
    const { data } = await api.get("/medications");
    return data;
  } catch (err) {
    await new Promise((r) => setTimeout(r, 1200));
    const { data } = await api.get("/medications");
    return data;
  }
}

export async function aiInferMedication(name) {
  const { data } = await api.post("/medications/ai-infer", { name });
  return data;
}

export async function addManualMedication(name) {
  const { data } = await api.post("/medications/manual", { name });
  return data;
}

// ---------- Public stats (landing + ticker + social proof) ----------
export async function fetchHomepageContent() {
  const { data } = await api.get("/homepage/content");
  return data;
}

export async function fetchPublicStats() {
  const { data } = await api.get("/stats/public");
  return data;
}

export async function fetchSocialProof(medicationId) {
  const params = medicationId ? { medication_id: medicationId } : {};
  const { data } = await api.get("/stats/social-proof", { params });
  return data; // { total_checks, total_users, checks_for_medication }
}

// Client-side guard: never render synthetic/dev check rows publicly.
const TEST_ROW_RX = /(^TEST_|^custom ingredient|notarealbrand|not_a_real|xyz_|\btest\b)/i;

export async function fetchRecentChecks(limit = 15) {
  const { data } = await api.get(`/stats/recent-checks?limit=${limit}`);
  if (data && Array.isArray(data.items)) {
    data.items = data.items.filter(
      (it) => !TEST_ROW_RX.test(`${it.medication || ""} ${it.product || ""}`),
    );
  }
  return data;
}

export async function fetchPairUsage({ medicationId, productId, productName }) {
  const params = new URLSearchParams();
  params.set("medication_id", medicationId);
  if (productId) params.set("product_id", productId);
  if (productName) params.set("product_name", productName);
  const { data } = await api.get(`/stats/pair-usage?${params.toString()}`);
  return data;
}

// Top medication × product combos checked in the last N days. Used by the
// homepage trending ticker.
export async function fetchTrendingChecks({ limit = 10, days = 7 } = {}) {
  const { data } = await api.get(
    `/stats/trending?limit=${limit}&days=${days}`,
  );
  return data; // { items: [...], window_days }
}

// 3 popular OTHER products checked against the same medication. Used on
// the results page to surface "Also check:" suggestions.
export async function fetchRelatedChecks({ medicationId, excludeProduct, limit = 3 } = {}) {
  const params = new URLSearchParams();
  params.set("medication_id", medicationId);
  if (excludeProduct) params.set("exclude_product", excludeProduct);
  params.set("limit", String(limit));
  const { data } = await api.get(`/stats/related-checks?${params.toString()}`);
  return data; // { items: [...] }
}

// Build a redirect URL for affiliate tracking (renders as href).
export function affiliateClickUrl(productId, retailer, source = "result") {
  return `${API}/affiliate/click?product_id=${encodeURIComponent(productId)}`
    + `&retailer=${encodeURIComponent(retailer)}`
    + `&source=${encodeURIComponent(source)}`;
}

// POST variant — logs the click (with optional medication_id / result_type
// for revenue attribution) and returns {success, redirect_url}. Falls back
// to the GET URL on any error so the buy button never blocks the user.
export async function affiliateClick({
  productId, retailer, source = "result",
  sessionId, medicationId, resultType,
} = {}) {
  try {
    const { data } = await api.post("/affiliate/click", {
      product_id: productId,
      retailer,
      source,
      session_id: sessionId,
      medication_id: medicationId,
      result_type: resultType,
    });
    return data;
  } catch (e) {
    return {
      success: false,
      redirect_url: affiliateClickUrl(productId, retailer, source),
      error: e?.response?.data?.detail || e.message,
    };
  }
}

export async function fetchIngredients() {
  const { data } = await api.get("/ingredients");
  return data;
}

export async function fetchProducts() {
  // One automatic retry — the catalog response is ≈400 KB and on flaky
  // mobile networks the first GET occasionally aborts mid-transfer.
  // Retrying once with a fresh axios call is dramatically more reliable
  // than asking the user to refresh.
  try {
    const { data } = await api.get("/products");
    return data;
  } catch (err) {
    // Wait a short beat for any transient network blip to clear, then retry.
    await new Promise((r) => setTimeout(r, 1200));
    const { data } = await api.get("/products");
    return data;
  }
}

export async function searchProducts(q, limit = 6) {
  const { data } = await api.get("/products/search", { params: { q, limit } });
  return data;
}

export async function resolveProductImage(productId) {
  const { data } = await api.get(`/products/${encodeURIComponent(productId)}/image`);
  return data;
}

// Map new /api/check response shape → legacy ResultCard shape so the existing
// UI keeps rendering without a full rewrite. Cutover-friendly adapter.
const RESULT_TO_RISK = { SAFE: "GREEN", CAUTION: "YELLOW", AVOID: "RED" };
const RISK_TO_RISK   = { HIGH: "RED",   MODERATE: "YELLOW", LOW: "GREEN" };
const HEADLINE_BY_RISK = {
  GREEN: "No label conflict found",
  YELLOW: "Use with caution",
  RED: "Avoid this combination",
};
// Verdict copy must follow the score-based ``verdict`` (what the gauge
// shows) so users never see "Use with caution" next to an AVOID gauge.
// Falls back to HEADLINE_BY_RISK[overall_result] for legacy / null verdicts.
const HEADLINE_BY_VERDICT = {
  SAFE:              "No label conflict found",
  MOSTLY_SAFE:       "No label conflict found",
  CAUTION:           "Use with caution",
  AVOID:             "Avoid this combination",
  INSUFFICIENT_DATA: "We couldn't verify this product's ingredients yet",
};

// Adapts the verified-source-only /api/check payload into the shape the
// ResultCard + share/related components consume. No score, no confidence, no
// AI-inferred anything — the backend only ever returns verified, sourced
// findings (or INSUFFICIENT_DATA).
function adaptCheckResponse(r) {
  if (!r || typeof r !== "object" || !r.verdict) return r;
  const med = r.medication || {};
  const meds = Array.isArray(r.medications) ? r.medications : [];
  const product = r.product || {};
  const alts = Array.isArray(r.safe_alternatives) ? r.safe_alternatives : [];

  const safer = alts.map((p) => ({
    id: p.id,
    name: p.name || p.full_name,
    brand: p.brand,
    full_name: p.full_name,
    key_ingredients: [],
    price_usd: p.price_usd,
    image_url: p.image_url,
    affiliate_amazon: p.affiliate_amazon,
    affiliate_sephora: p.affiliate_sephora,
    affiliate_ulta: p.affiliate_ulta,
    affiliate_target: p.affiliate_target,
    fragrance_free: p.fragrance_free,
    non_comedogenic: p.non_comedogenic,
    vegan: p.vegan,
    cruelty_free: p.cruelty_free,
    why_safer: p.why_safer || "Checked against this medication's label.",
  }));

  return {
    id: r.check_history_id,
    verdict: r.verdict,
    verdict_display: r.verdict_display || r.verdict,
    overall_result: r.overall_result || r.verdict,
    severity_tier: r.severity_tier || r.verdict,
    medication: {
      id: med.id,
      name: (med.brand_names && med.brand_names[0]) || med.generic_name || "",
      generic_name: med.generic_name || "",
      description: med.description || "",
      category: med.category || "",
      fda_label_url: med.fda_label_url || null,
    },
    medications: meds.map((m) => ({
      id: m.id,
      name: m.display_name || (m.brand_names && m.brand_names[0]) || m.generic_name || m.id,
      generic_name: m.generic_name || "",
      category: m.category || "",
    })),
    // Verified, sourced rows that produced the verdict.
    verified_findings: Array.isArray(r.verified_findings) ? r.verified_findings : [],
    uncovered_actives: Array.isArray(r.uncovered_actives) ? r.uncovered_actives : [],
    ingredient_chips: Array.isArray(r.ingredient_chips) ? r.ingredient_chips : [],
    active_categories: Array.isArray(r.active_categories) ? r.active_categories : [],
    ingredients_checked_count: r.ingredients_checked_count || 0,
    actives_checked_count: r.actives_checked_count || 0,
    unmatched_ingredients: Array.isArray(r.unmatched_ingredients) ? r.unmatched_ingredients : [],
    product_not_found: !!r.product_not_found,
    data_status_message: r.data_status_message || null,
    product,
    product_name: product.full_name || product.name || "Custom product",
    product_brand: product.brand || null,
    product_id: product.id || null,
    // No score is ever surfaced — kept null so legacy consumers stay quiet.
    safety_score: null,
    // Keep the raw verified alternatives intact under their canonical key so
    // the Discussion Report PDF (which reads `safe_alternatives`) renders them.
    safe_alternatives: alts,
    safe_alternatives_raw: alts,
    safer_alternatives: safer,
    safe_alternatives_locked: !!r.safe_alternatives_locked,
    safe_alternatives_count: r.safe_alternatives_count || 0,
    disclaimer: r.disclaimer,
    share_id: r.share_id || r.history_id || r.check_history_id || null,
    history_id: r.history_id || r.check_history_id || null,
    check_history_id: r.check_history_id || r.history_id || null,
  };
}

export async function runCheck(payload) {
  // Translate legacy frontend shape → new POST /api/check shape.
  const product_input = {};
  if (payload.product_id) product_input.product_id = payload.product_id;
  else if (payload.barcode) product_input.barcode = payload.barcode;
  else if (payload.custom_ingredients && payload.custom_ingredients.length)
    product_input.ingredient_text = payload.custom_ingredients.join(", ");
  else if (payload.product_query) product_input.search_query = payload.product_query;

  // When the user typed a free-form product label (and we ended up on the
  // ingredient_text path), forward it so the result card shows their label
  // instead of "Custom ingredient list".
  if (product_input.ingredient_text && payload.product_query) {
    product_input.product_name = payload.product_query;
  }

  const body = { product_input };
  if (Array.isArray(payload.medication_ids) && payload.medication_ids.length) {
    body.medication_ids = payload.medication_ids;
  } else if (payload.medication_id) {
    body.medication_id = payload.medication_id;
  }
  const { data } = await api.post("/check", body, { forceUserToken: true });
  return adaptCheckResponse(data);
}

export async function fetchCheckHistory() {
  const { data } = await api.get("/checks/history", { forceUserToken: true });
  return data;
}

// ---------- Pro-gated PDF download ----------
// Returns the binary PDF on success. Throws an axios error with status 402
// (and the standard `{error, message, upgrade_url}` payload) when the caller
// is anonymous or a non-Pro user.
export async function downloadDiscussionReportPdf(result) {
  const { data } = await api.post(
    "/report/pdf",
    { result },
    { responseType: "blob", forceUserToken: true },
  );
  return data; // Blob
}

// ---------- SEO interaction pages ----------
export async function fetchSeoInteraction(medication, ingredient) {
  const { data } = await api.get(
    `/seo/interaction/${encodeURIComponent(medication)}/${encodeURIComponent(ingredient)}`,
  );
  return data;
}

export async function fetchSeoInteractionByCombinedSlug(combined) {
  const { data } = await api.get(
    `/seo/interaction-by-slug/${encodeURIComponent(combined)}`,
  );
  return data;
}

export async function fetchSeoMedicationIndex(medication) {
  const { data } = await api.get(
    `/seo/interactions/${encodeURIComponent(medication)}`,
  );
  return data;
}

// ---------- Share card meta ----------
export async function fetchShareMeta(historyId) {
  const { data } = await api.get(`/share/meta/${encodeURIComponent(historyId)}`);
  return data;
}

// ---------- Profile preferences (Phase 1.16) ----------
export async function getPreferences() {
  const { data } = await api.get("/profile/preferences", { forceUserToken: true });
  return data;
}

export async function savePreferences(payload) {
  const { data } = await api.put("/profile/preferences", payload, { forceUserToken: true });
  return data;
}

// ---------- Routine Checker (Phase 1.15) ----------
export async function runRoutineCheck({ medication_ids, routine, remember = true }) {
  const { data } = await api.post("/routine/check", {
    medication_ids,
    routine,
    remember,
  }, { forceUserToken: true });
  return data;
}

export async function fetchRoutineSamples(medicationId) {
  const params = medicationId ? { medication_id: medicationId } : {};
  const { data } = await api.get("/routine/samples", { params });
  return data; // { medication: {id, name}, samples: [...] }
}

// ---------- Lightweight analytics (fire-and-forget) ----------
export function trackEvent(eventName, properties) {
  // Never await — analytics must not block UI.
  try {
    api
      .post("/events", { event_name: eventName, properties: properties || {} })
      .catch(() => {});
  } catch {
    // swallow — we never want analytics to break the page.
  }
}

// ---------- Share (P2) ----------
export function shareUrl(checkId) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return `${origin}/api/share/${checkId}`;
}

export async function fetchSharedCheck(checkId) {
  const { data } = await api.get(`/checks/${checkId}`);
  return data;
}

// ---------- Watchlist (P2) ----------
export async function getWatchlist() {
  const { data } = await api.get("/watchlist", { forceUserToken: true });
  return data;
}

export async function addWatchlistItem(ingredientId) {
  const { data } = await api.post("/watchlist", { ingredient_id: ingredientId }, { forceUserToken: true });
  return data;
}

export async function addWatchlistProduct(productId) {
  const { data } = await api.post("/watchlist", { product_id: productId }, { forceUserToken: true });
  return data;
}

export async function removeWatchlistItem(itemId, { kind = "ingredient" } = {}) {
  const { data } = await api.delete(
    `/watchlist/${itemId}?kind=${encodeURIComponent(kind)}`,
    { forceUserToken: true },
  );
  return data;
}

export async function getWatchlistNotifications(markSeen = true) {
  const { data } = await api.get(
    `/watchlist/notifications?mark_seen=${markSeen ? "true" : "false"}`,
    { forceUserToken: true },
  );
  return data;
}

// ---------- Saved routines (Phase Retention) ----------
export async function saveRoutine(payload) {
  const { data } = await api.post("/routines/save", payload, { forceUserToken: true });
  return data;
}

export async function listRoutines() {
  const { data } = await api.get("/routines", { forceUserToken: true });
  return data;
}

export async function fetchRoutine(routineId) {
  const { data } = await api.get(`/routines/${encodeURIComponent(routineId)}`, { forceUserToken: true });
  return data;
}

export async function deleteRoutine(routineId) {
  const { data } = await api.delete(`/routines/${encodeURIComponent(routineId)}`, { forceUserToken: true });
  return data;
}

// ---------- Browse (public "Does it react?" matrix) ----------
export async function browseIndex() {
  const { data } = await api.get("/browse/index");
  return data;
}

export async function browseMatrix({
  q = "",
  medicationId = "",
  ingredientId = "",
  riskLevel = "",
  page = 1,
  limit = 20,
} = {}) {
  const params = new URLSearchParams();
  if (q) params.set("q", q);
  if (medicationId) params.set("medication_id", medicationId);
  if (ingredientId) params.set("ingredient_id", ingredientId);
  if (riskLevel) params.set("risk_level", riskLevel);
  params.set("page", String(page));
  params.set("limit", String(limit));
  const { data } = await api.get(`/browse?${params.toString()}`);
  return data;
}

export async function browseMedication(medId) {
  const { data } = await api.get(`/browse/medication/${medId}`);
  return data;
}

export async function browseIngredient(ingId) {
  const { data } = await api.get(`/browse/ingredient/${ingId}`);
  return data;
}

export async function browsePair(medId, ingId) {
  const { data } = await api.get(`/browse/pair/${medId}/${ingId}`);
  return data;
}

// ---------- Barcode (consumer) ----------
export async function lookupBarcode(code) {
  const { data } = await api.post("/products/barcode-lookup", { barcode: code });
  return data;
}

// ---------- Admin ----------
export async function adminStatus() {
  const { data } = await api.get("/admin/status");
  return data;
}
export async function adminListMedications() {
  const { data } = await api.get("/admin/medications");
  return data;
}
export async function adminUpsertMedication(payload) {
  const { data } = await api.post("/admin/medications", payload);
  return data;
}
export async function adminDeleteMedication(id) {
  const { data } = await api.delete(
    `/admin/medications/${encodeURIComponent(id)}`,
  );
  return data;
}
export async function adminListProducts() {
  const { data } = await api.get("/admin/products");
  return data;
}
export async function adminUpsertProduct(payload) {
  const { data } = await api.post("/admin/products", payload);
  return data;
}
export async function adminDeleteProduct(id) {
  const { data } = await api.delete(
    `/admin/products/${encodeURIComponent(id)}`,
  );
  return data;
}

// ---------- Admin v2 (catalog: medications, ingredients, interactions, products) ----------
export async function catalogStatus() {
  const { data } = await api.get("/admin/v2/status");
  return data;
}
export async function catalogList(slug, { q = "", activeOnly = false, limit = 200 } = {}) {
  const params = new URLSearchParams();
  if (q) params.set("q", q);
  if (activeOnly) params.set("active_only", "true");
  params.set("limit", String(limit));
  const { data } = await api.get(`/admin/v2/${slug}?${params.toString()}`);
  return data;
}
export async function catalogGet(slug, id) {
  const { data } = await api.get(`/admin/v2/${slug}/${encodeURIComponent(id)}`);
  return data;
}
export async function catalogUpsert(slug, payload) {
  const { data } = await api.post(`/admin/v2/${slug}`, payload);
  return data;
}
export async function catalogDelete(slug, id, { hard = false } = {}) {
  const url = `/admin/v2/${slug}/${encodeURIComponent(id)}${hard ? "?hard=true" : ""}`;
  const { data } = await api.delete(url);
  return data;
}
export async function catalogPickerMedications() {
  const { data } = await api.get("/admin/v2/_picker/medications");
  return data;
}
// ---------- Launch gate (medication launch status + pharmacist audit) ----------
export async function fetchMedicationLaunch(id) {
  const { data } = await api.get(`/admin/v2/medications/${encodeURIComponent(id)}/launch`);
  return data;
}
export async function saveMedicationAudit(id, payload) {
  const { data } = await api.post(`/admin/v2/medications/${encodeURIComponent(id)}/audit`, payload);
  return data;
}
export async function launchMedication(id, note) {
  const { data } = await api.post(`/admin/v2/medications/${encodeURIComponent(id)}/launch`, { note });
  return data;
}
export async function unlaunchMedication(id, note) {
  const { data } = await api.post(`/admin/v2/medications/${encodeURIComponent(id)}/unlaunch`, { note });
  return data;
}
export async function spotCheckInteraction(id, payload) {
  const { data } = await api.post(`/admin/v2/verification/${encodeURIComponent(id)}/spot-check`, payload);
  return data;
}
export async function catalogPickerIngredients() {
  const { data } = await api.get("/admin/v2/_picker/ingredients");
  return data;
}
export async function catalogBulkImport(slug, { payload, dryRun = true, skipDuplicates = false } = {}) {
  const { data } = await api.post(`/admin/v2/bulk_import/${slug}`, {
    payload,
    dry_run: dryRun,
    skip_duplicates: skipDuplicates,
  });
  return data;
}

// Verification Queue (Phase 4)
export async function fetchVerificationQueue({ limit = 200, riskLevel, severity, medicationId, week, verified, sourcePending } = {}) {
  const params = new URLSearchParams();
  params.set("limit", String(limit));
  if (riskLevel) params.set("risk_level", riskLevel);
  if (severity) params.set("severity", severity);
  if (medicationId) params.set("medication_id", medicationId);
  if (week) params.set("week", String(week));
  if (verified === true) params.set("verified", "true");
  if (sourcePending === true) params.set("source_pending", "true");
  const { data } = await api.get(`/admin/v2/verification/queue?${params.toString()}`);
  return data;
}
export async function fetchVerificationWeekPlan() {
  const { data } = await api.get("/admin/v2/verification/week-plan");
  return data;
}
export async function rejectInteractionBulk(ids, reason) {
  const { data } = await api.post("/admin/v2/verification/reject-bulk", { ids, reason });
  return data;
}
export async function verifyInteraction(id, { notes, verifiedBy } = {}) {
  const { data } = await api.post(`/admin/v2/verification/${id}/verify`, {
    notes, verified_by: verifiedBy,
  });
  return data;
}
export async function rejectInteraction(id, reason) {
  const { data } = await api.post(`/admin/v2/verification/${id}/reject`, { reason });
  return data;
}
export async function editAndVerifyInteraction(id, patch) {
  const { data } = await api.post(`/admin/v2/verification/${id}/edit`, patch);
  return data;
}
export async function fetchVerificationHistory(id) {
  const { data } = await api.get(`/admin/v2/verification/${id}/history`);
  return data;
}
export async function previewVerificationReset() {
  const { data } = await api.post(`/admin/v2/verification/reset-verification`, { dry_run: true });
  return data;
}
export async function applyVerificationReset() {
  const { data } = await api.post(`/admin/v2/verification/reset-verification`, { confirm: true });
  return data;
}

// Phase 6 — Error Reports
export async function submitErrorReport(payload) {
  const { data } = await api.post("/error_reports", payload);
  return data;
}

// Report this result (public) — spec item 14
export async function submitResultReport(payload) {
  const { data } = await api.post("/result-reports", payload);
  return data;
}

// Provenance admin — coverage gaps, actives config, result reports
export async function fetchCoverageGaps() {
  const { data } = await api.get("/admin/provenance/coverage-gaps");
  return data;
}
export async function addIngredientAlias(token, ingredient) {
  const { data } = await api.post("/admin/provenance/ingredient-alias", { token, ingredient });
  return data;
}
export async function createCoverageRow(medicationId, ingredientId) {
  const { data } = await api.post("/admin/provenance/coverage-gaps/create-row", {
    medication_id: medicationId,
    ingredient_id: ingredientId,
  });
  return data;
}
export async function sendCoverageDigest() {
  const { data } = await api.post("/admin/provenance/coverage-digest/send", {});
  return data;
}
export async function fetchActivesConfig() {
  const { data } = await api.get("/admin/provenance/actives");
  return data;
}
export async function saveActivesConfig(categories) {
  const { data } = await api.put("/admin/provenance/actives", { categories });
  return data;
}
export async function fetchResultReports(status) {
  const qs = status ? `?status=${encodeURIComponent(status)}` : "";
  const { data } = await api.get(`/admin/provenance/result-reports${qs}`);
  return data;
}

// Layer 4 PHOTO — extract ingredient list from a label photo
const PHOTO_SESSION_KEY = "medskin.photo_session";
function ensurePhotoSession() {
  try {
    let s = localStorage.getItem(PHOTO_SESSION_KEY);
    if (!s) {
      s = (crypto?.randomUUID && crypto.randomUUID()) ||
          `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(PHOTO_SESSION_KEY, s);
    }
    return s;
  } catch (_e) {
    return `fallback-${Date.now()}`;
  }
}
export async function extractIngredientsFromPhoto(image, sessionId) {
  const s = sessionId || ensurePhotoSession();
  const { data } = await api.post(
    "/products/photo-extract",
    { image, session_id: s },
    { headers: { "x-medskin-session": s } },
  );
  return data;
}
export async function fetchPhotoOcrUsage() {
  const s = ensurePhotoSession();
  const { data } = await api.get("/products/photo-extract/usage", {
    headers: { "x-medskin-session": s },
  });
  return data; // {session_id, used, limit, remaining}
}
export async function fetchAdminErrorReports({ status, limit = 100 } = {}) {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  params.set("limit", String(limit));
  const { data } = await api.get(`/admin/v2/error_reports?${params.toString()}`);
  return data;
}
export async function triageErrorReport(id, { status, resolution_notes } = {}) {
  const { data } = await api.post(`/admin/v2/error_reports/${id}/triage`, {
    status, resolution_notes,
  });
  return data;
}

export async function submitIngredientList(productId, payload) {
  const { data } = await api.post(
    `/products/${encodeURIComponent(productId)}/ingredient-submissions`,
    payload,
  );
  return data;
}
export async function fetchAdminIngredientSubmissions({ status, limit = 100 } = {}) {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  params.set("limit", String(limit));
  const { data } = await api.get(`/admin/v2/ingredient_submissions?${params.toString()}`);
  return data;
}
export async function approveIngredientSubmission(id, { notes } = {}) {
  const { data } = await api.post(
    `/admin/v2/ingredient_submissions/${id}/approve`,
    { notes },
  );
  return data;
}
export async function rejectIngredientSubmission(id, { notes } = {}) {
  const { data } = await api.post(
    `/admin/v2/ingredient_submissions/${id}/reject`,
    { notes },
  );
  return data;
}

export async function submitBarcode(productId, payload) {
  const { data } = await api.post(
    `/products/${encodeURIComponent(productId)}/barcode-submissions`,
    payload,
  );
  return data;
}
export async function submitProductPhoto(productId, payload) {
  const { data } = await api.post(
    `/products/${encodeURIComponent(productId)}/photo-submissions`,
    payload,
  );
  return data;
}
export async function fetchAdminPhotoSubmissions(status) {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  const { data } = await api.get(`/admin/v2/photo_submissions?${params.toString()}`);
  return data;
}
export async function approvePhotoSubmission(id, payload) {
  const { data } = await api.post(`/admin/v2/photo_submissions/${id}/approve`, payload || {});
  return data;
}
export async function rejectPhotoSubmission(id, payload) {
  const { data } = await api.post(`/admin/v2/photo_submissions/${id}/reject`, payload || {});
  return data;
}
export async function fetchAdminBarcodeSubmissions({ status, limit = 100 } = {}) {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  params.set("limit", String(limit));
  const { data } = await api.get(`/admin/v2/barcode_submissions?${params.toString()}`);
  return data;
}
export async function approveBarcodeSubmission(id, { notes } = {}) {
  const { data } = await api.post(
    `/admin/v2/barcode_submissions/${id}/approve`,
    { notes },
  );
  return data;
}
export async function rejectBarcodeSubmission(id, { notes } = {}) {
  const { data } = await api.post(
    `/admin/v2/barcode_submissions/${id}/reject`,
    { notes },
  );
  return data;
}

// -- Data-quality (non-English ingredient list → Claude translation) ------- //
export async function fetchNonEnglishProducts() {
  const { data } = await api.get(`/admin/v2/data-quality/non-english-products`);
  return data;
}
export async function translateProductIngredients(productId) {
  const { data } = await api.post(`/admin/v2/data-quality/translate/${productId}`);
  return data;
}
export async function rollbackProductTranslation(productId) {
  const { data } = await api.post(`/admin/v2/data-quality/rollback/${productId}`);
  return data;
}

export async function createCheckoutSession(packageId = "pro_monthly") {
  // Checkout must bind to the signed-in USER session, never the admin token.
  if (!tokenStore.get()) {
    throw new Error("Please sign in with your account before upgrading.");
  }
  const { data } = await api.post(
    "/payments/checkout/session",
    { package_id: packageId, origin_url: window.location.origin },
    { forceUserToken: true },
  );
  return data; // { url, session_id }
}

export async function getCheckoutStatus(sessionId) {
  const { data } = await api.get(`/payments/checkout/status/${sessionId}`, { forceUserToken: true });
  return data;
}

// Single source of truth for the Pro price — read from Stripe server-side.
export async function fetchPricing() {
  const { data } = await api.get("/payments/pricing");
  return data; // { amount, currency, interval, formatted, configured }
}

// Reconcile Pro status from Stripe — safety net for a missed/failed webhook.
export async function syncSubscription() {
  const { data } = await api.post("/payments/sync", {}, { forceUserToken: true });
  return data; // { is_pro, synced }
}

// Open the Stripe Customer Portal to manage / cancel the subscription.
export async function createBillingPortalSession() {
  const { data } = await api.post("/payments/portal", {
    origin_url: window.location.origin,
  }, { forceUserToken: true });
  return data; // { url }
}

// Verified receipt for the post-checkout screen (by Stripe session id).
export async function fetchReceipt(sessionId) {
  const { data } = await api.get(`/payments/receipt/${sessionId}`, { forceUserToken: true });
  return data;
}

// All receipts for the signed-in user (Billing list).
export async function fetchReceipts() {
  const { data } = await api.get("/payments/receipts", { forceUserToken: true });
  return data.items || [];
}

// Download a receipt PDF (authenticated blob → browser download).
export async function downloadReceiptPdf(receiptNo) {
  const res = await api.get(`/payments/receipts/${receiptNo}/pdf`, {
    responseType: "blob",
    forceUserToken: true,
  });
  const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `blendsafe-receipt-${receiptNo}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
